import { Link } from '@lib/features/common';
import { Entry, isEntry } from '@lib/features/entries';
import { useGlobal, useView } from '@lib/store';
import { useNextHydrated } from '@lib/utils/createToggleStore';
import { fmtDate, fmtDateRange, fmtTimeRange, isDateRange } from '@lib/utils/format';
import { VTPerformance } from '@lib/vivaticket/parse/parsePerformanceList';
import { trimSlashes } from '@liquorice/allsorts-craftcms-nextjs';
import { isAfter } from 'date-fns';
import startOfToday from 'date-fns/startOfToday';
import { useTranslations } from 'next-intl';
import React from 'react';

export type EventEntryPerformance = {
  dateStart: string;
  dateEnd: string;
};

type EventEntryMeta = {
  /** Tickets are sold online */
  isTicketed: boolean;
  /** Included in admission */
  isFree: boolean;
  eventDateFormatted?: string;
  eventTicketString: string;
  eventTimesFormatted?: string[];
  buyTicketsLink?: Link;
  showTicketStr?: boolean;
  priceDescription?: string;
  dateDescription?: string;
  locationDescription?: string;
  eventAk?: string;
  eventUsesPerformances?: boolean;
  eventPerformances?: EventEntryPerformance[];
  eventPrice?: string;
  eventPricePrefix?: string;
};

/** Apply further parsing for Event entries */
export const useEvent = <T extends Entry>(
  maybeItem?: T
): Partial<Entry<'event'> & EventEntryMeta> => {
  const t = useTranslations('event');
  const view = useView();
  const eventSettings = useGlobal('eventSettings');
  const bookingUrl = eventSettings?.bookingUrl;
  const vivaticketUrl = eventSettings?.vivaticketUrl;

  /** Avoid date rendering mismatch */
  const hydrated = useNextHydrated();
  const item = maybeItem ? maybeItem : view;

  const showTicketStr = false;

  if (!isEntry(item, 'event')) return {};

  const { eventMeta, eventDateStart, eventDateEnd, isEverydayEvent, indexDisplay } = item;

  const priceDescription = eventMeta?.priceDescription ?? undefined;
  const dateDescription = eventMeta?.dateDescription ?? undefined;
  const locationDescription = eventMeta?.locationDescription ?? undefined;

  const eventPrice = item.vtEventPrice || undefined;
  const eventPricePrefix = item.vtEventPricePrefix || undefined;

  const isTicketDisplay = indexDisplay === 'ticketIndex';
  const isEventDisplay = indexDisplay === 'eventIndex';
  const isBothDisplay = indexDisplay === 'both';

  const isTicketedEvent = isEntry(item, 'event', 'ticketed');
  const isDefaultEvent = isEntry(item, 'event', 'default');

  // const isFree = !isTicketedEvent ? (isTicketed ? false : !!item.isFreeEvent) : false;
  const isFree = !isTicketedEvent ? !!item.isFreeEvent : false;
  const eventTimes = item?.eventTimes ?? null;

  const eventTimesFormatted =
    eventTimes?.reduce((result, item) => {
      if (!item) return result;

      const { start, end } = item;

      const formatted = fmtTimeRange(start, end, '-', { timeZoneName: 'short' });
      if (formatted) return [...result, formatted];

      return result;
    }, [] as string[]) ?? [];

  let buyTicketsUrl = bookingUrl ?? '';
  let buyTicketsLinkText = t('bookNowCta');

  let eventAk: string | undefined = undefined;
  let eventUsesPerformances = false;
  let eventPerformances: EventEntryPerformance[] = [];

  if (isTicketedEvent) {
    buyTicketsUrl = `${trimSlashes(vivaticketUrl ?? '')}${item.vtCode}`;
    eventAk = item.eventAk ?? undefined;
    eventUsesPerformances = !!eventAk && !!item.eventUsesPerformances;
    item.vtDescription;
    item.pageTitle = item.pageTitle || item.vtAltName || null;
    item.entrySummary = item.entrySummary || item.vtDescription || null;

    eventPerformances = (item.eventPerformances || []).reduce((result, item) => {
      if (!item) return result;

      const { dateStart, dateEnd } = item;

      if (!dateStart || !dateEnd) return result;

      const today = startOfToday();
      const dateEndTimeStamp = new Date(dateEnd);

      // Return only future or ongoing performances
      if (isAfter(dateEndTimeStamp, today)) {
        return [...result, { dateStart, dateEnd }];
      }

      return result;
    }, [] as EventEntryPerformance[]);

    // item.eventPerformances = item.eventPerformances || null;
  }

  if (isDefaultEvent && (isBothDisplay || isTicketDisplay) && item.linkCta) {
    buyTicketsUrl = item.linkCta.href;
    buyTicketsLinkText = item.linkCta.customText || buyTicketsLinkText;
  }

  const buyTicketsLink: Link = {
    href: buyTicketsUrl,
    children: buyTicketsLinkText,
    target: '_blank',
  };

  const hasDateRange = isDateRange(eventDateStart, eventDateEnd);
  // if (hasDateRange) console.log(eventDateStart, eventDateEnd);

  const eventDateFormatted = hydrated
    ? isEverydayEvent
      ? t('everyday')
      : hasDateRange
      ? t('fromDate', { date: fmtDate(eventDateStart) })
      : fmtDateRange(eventDateStart, eventDateEnd, ' - ')
    : '';

  const eventTicketString = !isFree ? t('ticketed') : t('notTicketed');

  return {
    ...item,
    eventAk,
    eventPrice,
    eventPricePrefix,
    isFree,
    isTicketed: !isEventDisplay,
    eventDateFormatted,
    eventTicketString,
    eventTimesFormatted,
    eventUsesPerformances,
    eventPerformances,
    buyTicketsLink,
    showTicketStr,
    priceDescription,
    dateDescription,
    locationDescription,
  };
};

export const useEventPerformances = (eventAk?: string | null) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [items, setItems] = React.useState<VTPerformance[] | null>(null);

  React.useEffect(() => {
    if (!eventAk) return setLoading(false);
    setLoading(true);
    setError(null);
    fetch(`/api/vivaticket/performances?eventAk=${eventAk}`)
      .then((res) => {
        if (res.ok) {
          res.json().then((data) => {
            setItems(data.items || []);
          });
        }
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventAk]);

  return { loading, error, items };
};
